import React, { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useCheckPermission } from 'cityads-v3/hooks/permissions'
import classNames from 'classnames'
import { BannerRotator } from 'cityads-v3/types/bannerRotator'
import { Offer } from 'cityads-v3/types/offers'
import { useBannerRotatorList } from './List.hooks'
import { TableColumn } from 'cityads-v3/components/Table/TableTypes'
import classes from './List.scss'

import ActionsButton from './ActionsButton'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import EmptyResults from 'cityads-v3/components/EmptyResults'
import EqualizerOutlinedIcon from '@mui/icons-material/EqualizerOutlined';
import GetCodeButton from './GetCodeButton'
import IconButton from '@mui/material/IconButton'
import Paginator from 'cityads-v3/components/Paginator/Paginator'
import Paper from '@mui/material/Paper'
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined'
import PreviewButton from './PreviewButton'
import Table from 'cityads-v3/components/Table/Table'
import Tooltip from 'cityads-v3/components/Tooltip/Tooltip'
import Typography from '@mui/material/Typography'

export type BannerRotatorListProps = {
  offerId?: Offer['id'],
  reloadTrigger?: number
}

export default function List({ offerId, reloadTrigger }: BannerRotatorListProps): JSX.Element {
  const { t } = useTranslation(['BannerRotator', 'main'])
  const checkPermission = useCheckPermission()

  const {
    bannerRotatorTools: [bannerRotatorTools, isLoading, isLoaded],
    paginator: [page, perpage, onPageChange, onPerpageChange],
    reload
  } = useBannerRotatorList(offerId);

  const tableCols: TableColumn<BannerRotator>[] = useMemo(() => (
    [
      {
        name: 'id',
        title: 'ID'
      },
      {
        name: 'name',
        title: t('create.name')
      },
      {
        name: 'added_at',
        title: t('main:added_date'),
        format: 'date'
      },
      {
        name: 'acts',
        title: '',
        formatter: (value, row) => {
          return (
            <Typography classes={{ root: classes.actionsColumn }}>
              <GetCodeButton id={row.id} />
              <PreviewButton id={row.id} />
              <Tooltip isLight title={t('main:statistics')}>
                <IconButton
                  component="a"
                  href={`/stat/filters/set/tools/${row.id}`}
                  rel="noopener noreferrer"
                  target="_blank"
                  size="large"
                >
                  <EqualizerOutlinedIcon />
                </IconButton>
              </Tooltip>
              <ActionsButton id={row.id} onDelete={reload} />
            </Typography>
          )
        },
      }
    ]
  ), [t, reload])

  useEffect(() => {
    if (reloadTrigger) {
      reload()
    }
  }, [
    reload,
    reloadTrigger
  ])

  if (isLoaded && !bannerRotatorTools?.meta.total) {
    return (
      <EmptyResults
        icon={<PhotoLibraryOutlinedIcon />}
        emptyText={
          <>
            <Typography gutterBottom>{t('noRotators')}</Typography>
            <Typography>{t('description')}</Typography>
          </>
        }
        actions={
          checkPermission('edit_banner_rotator') && !offerId ?
            <Button
              component={Link}
              to={'/publisher/tools/bannerRotator/create'}
            >{t('main:buttons.create')}</Button>
            : null
        }
      />
    )
  }

  if (isLoading && !bannerRotatorTools?.bannerRotator?.length) {
    return <CircularProgress />
  }

  return (
    <div className={classes.container}>
      {checkPermission('edit_banner_rotator') && !offerId && (
        <div className={classes.buttons}>
          <Button
            color="primary"
            component={Link}
            to="/publisher/tools/bannerRotator/create"
            variant="contained"
          >
            {t('main:buttons.create')}
          </Button>
        </div>
      )}

      <Paper className={classNames(classes.table, { [classes.isLoading]: isLoading })}>
        <Table
          blueTheme
          showRowHover
          cols={tableCols}
          rows={bannerRotatorTools?.bannerRotator}
        />

        <Paginator
          page={page}
          rowsPerPage={perpage}
          count={bannerRotatorTools?.meta.total ?? 0}
          onPageChange={onPageChange}
          onRowsPerPageChange={onPerpageChange}
        />
      </Paper>
    </div>
  )
}
